import { Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Card, Container, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import useDebounce from '../../../../hooks/useDebounce';
import { isExecutiveManager, renderUserOptions, showUserDisplayName } from '../../../../lib/userUtils';
import { fetchAssociatedTestCases } from '../../../../modules/associations';
import { bulkUpdateAssociations, removeAssociationsBulk } from '../../../../modules/testcycles';
import { getTestSuites } from '../../../../modules/testsuites';
import ConfirmationModal from '../../../elements/ConfirmationModal';
import { testCycleTestCaseTableHeaders } from '../../test-case/testCaseTableHead';
import AddExistingTestCaseModal from './AddExistingTestCaseModal';
import LinkedTestCaseActions from './LinkedTestCaseActions';
import LinkedTestCasesTable from './LinkedTestCasesTable';
import { Delete } from '@mui/icons-material';
const LinkedTestCasesComponent = ({ entityId, users, data: testCycle, fetchTestCycleData }) => {
  const { projectId } = useParams();
  const { addNotification } = useNotification();
  const [data, setData] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [selectedTestSuite, setSelectedTestSuite] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  // useEffect(() => {
  //   const fetchTestSuitesData = async () => {
  //     try {
  //       const response = await getTestSuites(projectId, 0, 'all', '', 'active');
  //       setTestSuites(response.data);
  //     } catch (error) {
  //       console.error('Error fetching test suites:', error);
  //     }
  //   };
  //   fetchTestSuitesData();
  // }, [projectId, testCycle]);

  const fetchLinkedTestCases = async (pageNumber = 1, search = '', testSuite = 'all') => {
    setLoading(true);
    try {
      const response = await fetchAssociatedTestCases(
        entityId,
        projectId,
        search,
        testSuite !== 'all' ? [testSuite] : [],
        'Approved',
        true,
        pageNumber,
        perPage,
      );
      setData(response?.associations || []);
      setTotalCount(response?.total || 0);
      setPage(pageNumber);
      if (response?.available_test_suites && response.available_test_suites.length > 0) {
      setTestSuites(response.available_test_suites);
    }
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to fetch linked test cases.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite);
  }, [
    entityId,
    projectId,
    debouncedSearchQuery,
    selectedTestSuite,
    perPage,
    page
  ]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };
  const handleEditClick = () => {
    setIsEditing(true);
    const initialEditData = {};
    data.forEach(tc => {
      initialEditData[tc.id] = {
        id: tc.id,
        assigned_to: tc.assigned_to || '',
        environment: tc.environment?.id || '',
      };
    });
    setEditFormData(initialEditData);
  };

  const handleSaveClick = async () => {
    const associations = Object.entries(editFormData).map(([id, fields]) => ({
      id,
      assignedTo: fields.assigned_to || null,
      environment: fields.environment || null,
    }));

    try {
      await bulkUpdateAssociations({ associations }, entityId);
      setIsEditing(false);
      fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite);
      addNotification('success', 'Test cases updated successfully!');
      setSelected([]);
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to update test cases.');
    }
  };

  const handleDelete = async () => {
    try {
      await removeAssociationsBulk({ testcases: selected }, projectId);
      setShowConfirm(false);
      fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite);
      addNotification('success', 'Test cases deleted successfully!');
      setSelected([]);
      setIsEditing(false);
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to delete test cases.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    fetchLinkedTestCases();
    setSelected([]);
  };

  const handleDropdownChange = (event, fieldName, rowId) => {
    setEditFormData(prev => ({
      ...prev,
      [rowId]: { ...prev[rowId], [fieldName]: event.target.value },
    }));
  };

  const handleBulkDropdownChange = (event, fieldName) => {
    const updatedEditFormData = {};
    selected.forEach(rowId => {
      updatedEditFormData[rowId] = {
        ...editFormData[rowId],
        [fieldName]: event.target.value,
      };
    });
    setEditFormData(prev => ({ ...prev, ...updatedEditFormData }));
  };

  const getEnvironment = (executions) => {
    const envs = executions?.map(ex => ex?.iteration_details?.environment_details?.name) || [];
    return envs.length ? [...new Set(envs)].join(', ') : 'None';
  };

  const getIterations = (executions) => {
    const iterations = executions?.map(ex => ex?.iteration_details?.name) || [];
    return iterations.length ? [...new Set(iterations)].join(', ') : 'None';
  };

  const getTestSuitesText = (suites) => {
    return suites.map(suite => testSuites.find(a => a.id === suite)?.name).join(', ');
  };

  const formattedData = useMemo(() => {
    return data.map(row => ({
      ...row,
      key: row?.testcase?.key,
      status: row?.testcase?.status_details?.name,
      statusColor: row?.testcase?.status_details?.color,
      module: row?.testcase?.module_details?.name,
      description: row?.testcase?.summary,
      environment: getEnvironment(row?.test_case_executions),
      assignedTo: showUserDisplayName(row?.assigned_to, users),
      executionStatus: row?.test_case_executions[0]?.status_details || 'No Executions',
      iterations: getIterations(row?.test_case_executions),
      suites: getTestSuitesText(row?.testcase?.test_suites),
    }));
  }, [data, testSuites]);

  return (
    <>
      <Container fluid className="px-0">
        <Card className="shadow-sm mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center" style={{ height: '50px' }}>
            <h5 className="mb-0">Test cases</h5>
            <div className="d-flex align-items-center justify-content-end gap-2">
              {formattedData.length > 0 && <span className="text-muted me-3">Total: {totalCount}</span>}
              {testCycle?.completion_date === null && !isExecutiveManager() && (
                <Button variant="contained" color="primary" onClick={() => setShowModal(true)}>
                  + Test Cases
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {!isExecutiveManager() && (
              <div className="d-flex flex-wrap mb-4">
                {data.length > 1 && selected.length < 2 && (
                  <div
                    className="w-100 p-2 mt-2"
                    style={{
                      borderLeft: '4px solid #6c757d',
                      backgroundColor: '#f8f9fa',
                    }}>
                    <p className="m-0" style={{ color: '#6c757d' }}>
                      Note: Select two or more test cases for bulk editing
                      options.
                    </p>
                  </div>
                )}

                {selected.length > 0 && (
                  <div className="d-flex align-items-center gap-2 w-100">
                    {selected.length > 1 && (
                      <>
                        <Button
                          title="Delete selected test case"
                          variant="contained"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => setShowConfirm(true)}>
                          Delete
                        </Button>
                        <Form.Group>
                          <Form.Label visuallyHidden>
                            Assign Environment
                          </Form.Label>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label visuallyHidden>Assign Tester</Form.Label>
                          <Form.Select
                            title="Assign Tester"
                            onChange={(e) =>
                              handleBulkDropdownChange(e, 'assigned_to')
                            }
                            defaultValue=""
                            style={{ width: '180px', minHeight: '38px' }}>
                            <option value="" disabled>
                              Assign Tester
                            </option>
                            {renderUserOptions(users)}
                          </Form.Select>
                        </Form.Group>
                        {isEditing ? (
                          <>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={handleSaveClick}
                              disabled={loading}>
                              Save
                            </Button>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={handleCancel}>
                              Cancel
                            </Button>
                          </>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            <AddExistingTestCaseModal
              show={showModal}
              handleClose={() => setShowModal(false)}
              fetchLinkedTestCases={() => fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite)}
              entityId={entityId}
              linkedTestSuites={testCycle?.test_suites || []}
              fetchTestCycleData={fetchTestCycleData}
            />
            <LinkedTestCaseActions
              searchValue={searchValue}
              setSearchValue={handleSearch}
              testSuites={testSuites}
              selectedTestSuite={selectedTestSuite}
              setSelectedTestSuite={setSelectedTestSuite}
              data={data}
              allLinkedTestCases={data}
            />
            <LinkedTestCasesTable
              data={formattedData}
              isEditing={isEditing}
              editFormData={editFormData}
              handleDropdownChange={handleDropdownChange}
              handleCheckboxChange={(id) => {
                setSelected(prev => {
                  const updated = prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id];
                  setIsEditing(updated.length > 1);
                  return updated;
                });
              }}
              headerTabs={testCycleTestCaseTableHeaders}
              selectedItems={selected}
              setSelectedItems={setSelected}
              users={users}
              loading={loading}
              handleEditClick={handleEditClick}
              handleSaveClick={handleSaveClick}
              handleBulkDropdownChange={handleBulkDropdownChange}
              setShowConfirm={setShowConfirm}
              setIsEditing={setIsEditing}
              isCompleted={testCycle?.completion_date !== null || testCycle?.status?.name === 'Completed'}
              showPages
              handlePageChange={handlePageChange}
              totalCount={totalCount}
              page={page}
              perPage={perPage}
              setPerPage={handlePageSizeChange}
            />
          </Card.Body>
        </Card>
      </Container>
      <ConfirmationModal
        show={showConfirm}
        handleConfirm={handleDelete}
        handleClose={() => setShowConfirm(false)}
        title="Remove Linked Test Cases"
        body="Are you sure you want to remove the selected test case(s) and previous executions?"
        loading={loading}
      />
    </>
  );
};

export default LinkedTestCasesComponent;
